/* import __COLOCATED_TEMPLATE__ from './phone-dialler-contact-selectors.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type TaskGenerator } from 'ember-concurrency';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import type SearchableDocument from 'embercom/objects/inbox/searchable-document';
import type TeamSummary from 'embercom/objects/inbox/team-summary';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import type InboxSearchSuggestionsService from 'embercom/services/inbox-search-suggestions-service';
import type Inbox2AssigneeSearch from 'embercom/services/inbox2-assignee-search';
import { ChannelAvailabilityCode } from 'embercom/services/inbox2-teammate-activity-service';
import type IntercomCallService from 'embercom/services/intercom-call-service';
import { isValidE164PhoneNumber } from 'embercom/utils/phone-number';
import type { PhoneReusableWorkflow } from 'embercom/objects/phone/reusable-workflow';
import type RouterService from '@ember/routing/router-service';
import type Session from 'embercom/services/session';
import safeWindowOpen from 'embercom/lib/safe-window-open';
// FLAG-CLEANUP: team-phone-preserve-channel-availability-on-nexus
// Diagnostic import added 2026-05-29 for intercom#506170. When removing the flag,
// delete this import line (Sentry is not used elsewhere in this component).
import { captureMessage } from '@sentry/browser';

// FLAG-CLEANUP: team-phone-preserve-channel-availability-on-nexus
// Module-level dedup keyed on (adminId → last-logged exclusion shape). Logs once
// per unique exclusion shape per admin per browser session — so a teammate
// repeatedly going away+coming-back+going-away stays silent (same shape, no new
// diagnostic info), but a shape change (e.g. awayMode → awayMode+channelAvailability
// because the bug fired) re-logs. Resets only on full page reload. When removing
// the flag, delete this.
const _lastLoggedShapeByAdmin = new Map<number, string>();

type FavoriteChip =
  | {
      type: 'admin';
      id: number;
      name: string;
      admin: AdminSummary;
      onClick: () => void;
      onRemove: () => void;
    }
  | {
      type: 'team';
      id: number;
      name: string;
      team: TeamSummary;
      onClick: () => void;
      onRemove: () => void;
    }
  | { type: 'workflow'; id: number; name: string; onClick: () => void; onRemove: () => void }
  | {
      type: 'user';
      id: string;
      name: string;
      user: UserSummary;
      onClick: () => void;
      onRemove: () => void;
    };

const CONTACT_LIST_ORDER: ReadonlyArray<FavoriteChip['type']> = [
  'admin',
  'team',
  'user',
  'workflow',
];

const FAVORITE_TYPE_ORDER: Record<FavoriteChip['type'], number> = Object.fromEntries(
  CONTACT_LIST_ORDER.map((type, index) => [type, index]),
) as Record<FavoriteChip['type'], number>;

interface Signature {
  Element: HTMLElement;
  Args: {
    searchPlaceholder: string;
    showAdminsList: boolean;
    showTeamsList: boolean;
    showUsersList: boolean;
    showWorkflowsList?: boolean;
    workflowsList?: PhoneReusableWorkflow[];
    workflowsLoadFailed?: boolean;
    isActivelyCallingId?: string | number;
    isActivelyCalling: boolean;
    actionLabel: string;
    actionProcessingLabel: string;
    onAdminListActionClicked: (admin: AdminSummary) => void;
    onTeamListActionClicked: (team: TeamSummary) => void;
    onUserListActionClicked: (user: UserSummary) => void;
    onWorkflowListActionClicked: (workflow: PhoneReusableWorkflow) => void;
    showFavorites?: boolean;
    favoriteTransferTargets?: Array<{ type: string; id: string | number }>;
    onToggleFavorite?: (type: string, id: string | number) => void;
  };
}

export default class PhoneDiallerContactSelectors extends Component<Signature> {
  @service declare inboxSearchSuggestionsService: InboxSearchSuggestionsService;
  @service declare inbox2AssigneeSearch: Inbox2AssigneeSearch;
  @service declare intercomCallService: IntercomCallService;
  @service declare router: RouterService;
  @service declare session: Session;

  @tracked userSearchQuery = '';
  @tracked userInitialList: UserSummary[] = [];
  @tracked userSearchResults: UserSummary[] = [];
  @tracked isLoadingUserSearchResults = false;
  @tracked transferOptionsLoadFailed = false;
  @tracked userSearchLoadFailed = false;
  @tracked hasHydratedFavorites = false;

  get adminsForApp(): Array<AdminSummary> {
    let allAdmins = this.inbox2AssigneeSearch.searchableAdmins;
    let adminSummaryObjects = allAdmins.map(
      (adminSearchableDocument: SearchableDocument) => adminSearchableDocument.data as AdminSummary,
    );

    // FLAG-CLEANUP: team-phone-preserve-channel-availability-on-nexus
    // Diagnostic for intercom#506170. When removing the flag, delete this entire block.
    //
    // Anchors on the symptom — "admin excluded from transfer search" — not on any specific
    // bug shape. For every admin that fails the filter below, captures full state to Sentry
    // so support can search by adminId and diagnose any future variant of this bug class
    // (missing channelAvailability, corrupted away_mode, unexpected restrictedContexts, etc.)
    // without code changes.
    //
    // Dedup is per (adminId, exclusion-shape) via module-level _lastLoggedShapeByAdmin —
    // the shape excludes `nameMatch` (user-driven UI noise from typing in the search box).
    // Same-shape re-occurrence (e.g. teammate goes away → online → away again) is silent
    // because the prior event already captured that exact state. A shape change re-logs.
    //
    // Residual silent case: if a dynamic value within an unchanged shape category corrupts
    // (e.g. awayModeEnabled flips from `true` to `undefined` — both produce shape
    // ['awayModeEnabled']), we will not re-log. Accepted as acceptable risk — rare and
    // backend Snowflake logs cover the cross-check.
    if (
      this.session.workspace.isFeatureEnabled('team-phone-preserve-channel-availability-on-nexus')
    ) {
      let searchQuery = this.userSearchQuery.toLowerCase();
      for (let admin of adminSummaryObjects) {
        let excludedBy: string[] = [];
        if (admin.isUnassignedAssignee) {
          excludedBy.push('isUnassignedAssignee');
        }
        if ((admin.restrictedContexts?.length ?? 0) !== 0) {
          excludedBy.push('restrictedContexts');
        }
        if (admin.adminStatus?.awayModeEnabled !== false) {
          excludedBy.push('awayModeEnabled');
        }
        let ca = admin.adminStatus?.channelAvailability;
        if (ca !== ChannelAvailabilityCode.Both && ca !== ChannelAvailabilityCode.Phone) {
          excludedBy.push('channelAvailability');
        }
        if (!admin.name.toLowerCase().includes(searchQuery)) {
          excludedBy.push('nameMatch');
        }

        let dedupRelevant = excludedBy.filter((reason) => reason !== 'nameMatch');
        if (dedupRelevant.length > 0) {
          let shapeKey = dedupRelevant.sort().join(',');
          if (_lastLoggedShapeByAdmin.get(admin.id) !== shapeKey) {
            _lastLoggedShapeByAdmin.set(admin.id, shapeKey);
            captureMessage(
              // eslint-disable-next-line @intercom/intercom/no-bare-strings
              'Admin excluded from phone transfer search',
              {
                level: 'warning',
                tags: {
                  feature: 'phone-transfer-search',
                  workspaceId: this.session.workspace.id,
                  adminId: String(admin.id),
                },
                extra: {
                  adminId: admin.id,
                  adminName: admin.name,
                  isUnassignedAssignee: admin.isUnassignedAssignee,
                  restrictedContexts: admin.restrictedContexts,
                  hasAdminStatus: !!admin.adminStatus,
                  awayModeEnabled: admin.adminStatus?.awayModeEnabled,
                  channelAvailability: admin.adminStatus?.channelAvailability,
                  isAutoChanged: admin.adminStatus?.isAutoChanged,
                  reassignConversations: admin.adminStatus?.reassignConversations,
                  searchQuery: this.userSearchQuery,
                  excludedBy,
                },
              },
            );
          }
        }
      }
    }

    adminSummaryObjects = adminSummaryObjects.filter(
      (admin) =>
        !admin.isUnassignedAssignee &&
        admin.restrictedContexts?.length === 0 &&
        admin.adminStatus?.awayModeEnabled === false &&
        (admin.adminStatus?.channelAvailability === ChannelAvailabilityCode.Both ||
          admin.adminStatus?.channelAvailability === ChannelAvailabilityCode.Phone) &&
        admin.name.toLowerCase().includes(this.userSearchQuery.toLowerCase()),
    );
    return adminSummaryObjects;
  }

  get transferTeams(): Array<TeamSummary> {
    let teamSearchableDocuments = this.inbox2AssigneeSearch.searchableTeams;
    let teamSummaryObjects = teamSearchableDocuments.map(
      (teamSearchableDocument: SearchableDocument) => teamSearchableDocument.data as TeamSummary,
    );
    teamSummaryObjects = teamSummaryObjects.filter(
      (team) =>
        !team.isUnassignedAssignee &&
        team.name.toLowerCase().includes(this.userSearchQuery.toLowerCase()),
    );

    return teamSummaryObjects.sort((a, b) => a.name.localeCompare(b.name));
  }

  get transferWorkflows(): PhoneReusableWorkflow[] {
    if (!this.args.workflowsList) {
      return [];
    }

    return this.args.workflowsList.filter(
      (workflow) =>
        workflow.title?.toLowerCase().includes(this.userSearchQuery.toLowerCase()) ?? false,
    );
  }

  get filteredFavorites(): FavoriteChip[] {
    if (!this.args.showFavorites || !this.args.favoriteTransferTargets?.length) {
      return [];
    }
    let chips: FavoriteChip[] = [];
    for (let target of this.args.favoriteTransferTargets) {
      let chip = this.resolveFavoriteChip(target);
      if (chip) {
        chips.push(chip);
      }
    }
    return chips.sort((a, b) => FAVORITE_TYPE_ORDER[a.type] - FAVORITE_TYPE_ORDER[b.type]);
  }

  private resolveFavoriteChip(target: { type: string; id: string | number }): FavoriteChip | null {
    let onToggle = this.args.onToggleFavorite;
    switch (target.type) {
      case 'admin': {
        if (!this.args.showAdminsList) {
          return null;
        }
        let id = Number(target.id);
        let admin = this.adminsForApp.find((a) => Number(a.id) === id);
        if (!admin) {
          return null;
        }
        return {
          type: 'admin',
          id,
          name: admin.name,
          admin,
          onClick: () => this.args.onAdminListActionClicked(admin),
          onRemove: () => onToggle?.('admin', id),
        };
      }
      case 'team': {
        if (!this.args.showTeamsList) {
          return null;
        }
        let id = Number(target.id);
        let team = this.transferTeams.find((t) => Number(t.id) === id);
        if (!team) {
          return null;
        }
        return {
          type: 'team',
          id,
          name: team.name,
          team,
          onClick: () => this.args.onTeamListActionClicked(team),
          onRemove: () => onToggle?.('team', id),
        };
      }
      case 'workflow': {
        if (!this.args.showWorkflowsList) {
          return null;
        }
        let id = Number(target.id);
        let workflow = this.transferWorkflows.find((w) => Number(w.id) === id);
        if (!workflow) {
          return null;
        }
        return {
          type: 'workflow',
          id,
          name: workflow.title ?? '',
          onClick: () => this.args.onWorkflowListActionClicked(workflow),
          onRemove: () => onToggle?.('workflow', id),
        };
      }
      case 'user': {
        if (!this.args.showUsersList) {
          return null;
        }
        let id = String(target.id);
        let user = this.knownUsers.find((u) => String(u.id) === id);
        if (!user) {
          return null;
        }
        let query = this.userSearchQuery.toLowerCase();
        if (query && !user.displayAs.toLowerCase().includes(query)) {
          return null;
        }
        return {
          type: 'user',
          id,
          name: user.displayAs,
          user,
          onClick: () => this.args.onUserListActionClicked(user),
          onRemove: () => onToggle?.('user', id),
        };
      }
      default:
        return null;
    }
  }

  get knownUsers(): UserSummary[] {
    let seen = new Set<string>();
    let combined: UserSummary[] = [];
    for (let list of [this.userSearchResults, this.userInitialList]) {
      for (let user of list) {
        let id = String(user.id);
        if (!seen.has(id)) {
          seen.add(id);
          combined.push(user);
        }
      }
    }
    return combined;
  }

  get favoritesCount(): number {
    return this.visibleFavoriteTargetCount;
  }

  get visibleFavoriteTargetCount(): number {
    let targets = this.args.favoriteTransferTargets ?? [];
    return targets.filter((t) => this.isFavoriteTypeVisibleInMode(t.type)).length;
  }

  isFavoriteTypeVisibleInMode(type: string): boolean {
    if (type === 'admin') {
      return this.args.showAdminsList;
    }
    if (type === 'team') {
      return this.args.showTeamsList;
    }
    if (type === 'workflow') {
      return this.args.showWorkflowsList ?? false;
    }
    if (type === 'user') {
      return this.args.showUsersList;
    }
    return false;
  }

  get displayedFavorites(): FavoriteChip[] {
    return this.hasHydratedFavorites ? this.filteredFavorites : [];
  }

  get favoritePlaceholderSlots(): number[] {
    if (this.hasHydratedFavorites) {
      return [];
    }
    return Array.from({ length: this.visibleFavoriteTargetCount }, (_, i) => i);
  }

  get favoriteTargetIds(): string[] {
    let ids: string[] = [];
    for (let target of this.args.favoriteTransferTargets ?? []) {
      ids.push(`${target.type}-${target.id}`);
    }
    return ids;
  }

  get isRefreshingTransferOptions() {
    return taskFor(this.refreshTransferOptions).isRunning;
  }

  get isLoading() {
    if (this.isLoadingUserSearchResults) {
      return true;
    }

    if ((this.args.showTeamsList || this.args.showAdminsList) && this.isRefreshingTransferOptions) {
      return true;
    }

    return false;
  }

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    if (this.args.showTeamsList || this.args.showAdminsList) {
      taskFor(this.refreshTransferOptions).perform();
    }

    if (this.args.showUsersList) {
      taskFor(this.onQueryChanged).perform(this.userSearchQuery);
    }
  }

  @restartableTask *refreshTransferOptions(): TaskGenerator<void> {
    yield this.inbox2AssigneeSearch.loadAllAdminsAndTeams();
    this.transferOptionsLoadFailed = this.inbox2AssigneeSearch.loadFailed;
    this.markFavoritesHydratedIfReady();
  }

  markFavoritesHydratedIfReady() {
    if (this.hasHydratedFavorites) {
      return;
    }
    let needsAdminsTeams = this.args.showAdminsList || this.args.showTeamsList;
    let needsUsers = this.args.showUsersList;
    let adminsTeamsReady = !needsAdminsTeams || !this.isRefreshingTransferOptions;
    let usersReady = !needsUsers || this.userInitialList.length > 0;
    if (adminsTeamsReady && usersReady) {
      this.hasHydratedFavorites = true;
    }
  }

  @restartableTask *onQueryChanged(query: string): TaskGenerator<void> {
    this.userSearchQuery = query;
    this.userSearchResults = [];
    if (!query && this.userInitialList.length > 0) {
      this.userSearchResults = this.userInitialList;
      this.isLoadingUserSearchResults = false;
      return;
    }
    this.isLoadingUserSearchResults = true;
    this.userSearchLoadFailed = false;

    try {
      let users: UserSummary[] =
        yield this.inboxSearchSuggestionsService.getUserSuggestionsForDialler(
          this.userSearchQuery,
          10,
          'contains',
        );
      if (this.userInitialList.length === 0) {
        this.userInitialList = users;
      }
      this.userSearchResults = users;
      this.markFavoritesHydratedIfReady();

      if (users.length === 0) {
        this.intercomCallService.registerCallEvent({
          action: 'user_search',
          object: 'dialler_modal',
          place: 'user_search',
          context: 'no_results',
        });
      } else {
        this.intercomCallService.registerCallEvent({
          action: 'user_search',
          object: 'dialler_modal',
          place: 'user_search',
          context: 'results_available',
        });
      }
    } catch (e: unknown) {
      this.userSearchResults = [];
      this.userSearchLoadFailed = true;
    }
    this.isLoadingUserSearchResults = false;
  }

  checkPhoneNumberValid(userSummary: UserSummary) {
    return isValidE164PhoneNumber(userSummary.phone || '');
  }

  @action
  openWorkflowInNewTab(workflowId: number, event: MouseEvent) {
    event.stopPropagation();
    safeWindowOpen(
      this.router.urlFor(
        'apps.app.automation.workflows.edit',
        this.session.workspace.id,
        workflowId,
        {
          queryParams: {
            visual: 'enabled',
          },
        },
      ),
      '_blank',
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Calling::PhoneDiallerContactSelectors': typeof PhoneDiallerContactSelectors;
    'inbox2/calling/phone-dialler-contact-selectors': typeof PhoneDiallerContactSelectors;
  }
}
